import {
    animate,
    AnimationTriggerMetadata,
    query,
    stagger,
    state,
    style,
    transition,
    trigger
} from '@angular/animations';
import { CUBIC_BEZIER_DEFAULT } from '@shared/animations/animations';
import { ListStaggerAnimationConfig, TransformAnimationConfig } from '@shared/animations/animations.models';

export const transitionTiming = (duration: string, timing: string = CUBIC_BEZIER_DEFAULT): string =>
    `${duration} ${timing}`;

export const transform = (config: TransformAnimationConfig): AnimationTriggerMetadata =>
    trigger(config.trigger || 'transform', [
        transition(config.stateChangeEnter || ':enter', [
            style({
                opacity: config.fadeIn !== undefined ? (config.fadeIn ? 0 : 1) : 0,
                transform: `${config.property}(${config.valueFrom})`
            }),
            animate(
                `${config.duration || '0.3s'} ${config.delay || '0s'} ${config.timing || CUBIC_BEZIER_DEFAULT}`,
                style({
                    opacity: 1,
                    transform: `${config.property}(${config.valueTo})`
                })
            )
        ]),
        transition(config.stateChangeLeave || ':leave', [
            style({
                opacity: 1,
                transform: `${config.property}(${config.valueTo})`
            }),
            animate(
                `${config.duration || '0.3s'} ${config.delay || '0s'} ${config.timing || CUBIC_BEZIER_DEFAULT}`,
                style({
                    opacity: config.fadeIn !== undefined ? (config.fadeIn ? 0 : 1) : 0,
                    transform: `${config.property}(${config.valueFrom})`
                })
            )
        ])
    ]);

export const listStagger = (config: ListStaggerAnimationConfig): AnimationTriggerMetadata =>
    trigger(config.trigger || 'listStagger', [
        transition('* => *', [
            query(
                ':leave',
                [
                    stagger(config.stagger, [
                        animate(
                            transitionTiming(`${config.duration || '0.3s'} ${config.delay || '0s'}`),
                            style({
                                opacity: config.fadeIn !== undefined ? (config.fadeIn ? 0 : 1) : 0,
                                transform: `${config.property}(${config.valueFrom})`
                            })
                        )
                    ])
                ],
                { optional: true }
            ),
            query(
                ':enter',
                [
                    style({
                        opacity: config.fadeIn !== undefined ? (config.fadeIn ? 0 : 1) : 0,
                        transform: `${config.property}(${config.valueFrom})`
                    }),
                    stagger(config.stagger, [
                        animate(
                            transitionTiming(`${config.duration || '0.3s'} ${config.delay || '0s'}`),
                            style({
                                opacity: 1,
                                transform: `${config.property}(${config.valueTo})`
                            })
                        )
                    ])
                ],
                { optional: true }
            )
        ])
    ]);

export const opacityTransition = (
    durationIn = '0.3s',
    durationOut = '0.3s',
    delayIn = '0s',
    delayOut = '0s'
): AnimationTriggerMetadata =>
    trigger('fadeTransition', [
        transition(':enter', [style({ opacity: 0 }), animate(transitionTiming(durationIn, delayIn))]),
        transition(':leave', [animate(transitionTiming(durationOut, delayOut), style({ opacity: 0 }))]),
        state('*', style({ opacity: 1 }))
    ]);

export const heightTransition: AnimationTriggerMetadata = trigger('heightTransition', [
    transition(':enter', [
        style({ opacity: 0, height: 0, overflow: 'hidden' }),
        animate(
            '100ms',
            style({
                opacity: 1,
                height: '*'
            })
        )
    ]),
    transition(':leave', [animate('100ms', style({ opacity: 0, height: 0 }))]),
    state('*', style({ overflow: 'hidden' }))
]);
